import React, { forwardRef, useId, useRef, useState } from 'react';
import { Icon } from './icons';
import { Button, cx } from './primitives';
import { Listbox, type ListOption } from './Listbox';
export const Input = forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(function Input({ className, type, ...props }, ref) {
    return <input {...props} ref={ref} type={type} className={cx(type === 'checkbox' || type === 'radio' ? 'bs-native-check' : type === 'range' ? 'bs-native-range' : 'bs-input', className)} />;
});
function optionsFromChildren(children: React.ReactNode): ListOption[] {
    return React.Children.toArray(children).flatMap((child) => {
        if (!React.isValidElement<{ value?: string | number; disabled?: boolean; children?: React.ReactNode }>(child) || child.type !== 'option') return [];
        const text = Array.isArray(child.props.children) ? child.props.children.join('') : child.props.children;
        const label = text == null ? '' : String(text);
        const value = child.props.value === undefined ? label : String(child.props.value);
        return [{ value, label, disabled: Boolean(child.props.disabled) }];
    });
}
export const Select = forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(function Select({ className, children, onChange, value, defaultValue, disabled, required, name, id, form, 'aria-label': ariaLabel }, _ref) {
    return <Listbox className={className} id={id} name={name} form={form} value={value === undefined ? undefined : String(value)} defaultValue={defaultValue === undefined ? undefined : String(defaultValue)} options={optionsFromChildren(children)} disabled={disabled} required={required} aria-label={ariaLabel} onChange={event => onChange?.(event as React.ChangeEvent<HTMLSelectElement>)} />;
});
export const Textarea = forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(function Textarea({ className, ...props }, ref) {
    return <textarea {...props} ref={ref} className={cx('bs-input bs-textarea', className)} />;
});
interface FieldCommon {
    label: string;
    hint?: string;
    error?: string;
}
export interface TextFieldProps extends React.InputHTMLAttributes<HTMLInputElement>, FieldCommon {
}
export const TextField = forwardRef<HTMLInputElement, TextFieldProps>(function TextField({ label, hint, error, id: passedId, className, ...props }, ref) {
    const uid = useId(), id = passedId || uid, desc = (error || hint) ? `${id}-desc` : undefined;
    return <div className={cx('bs-field', className)}><label htmlFor={id}>{label}{props.required && <span aria-hidden="true"> *</span>}</label><input {...props} ref={ref} id={id} className="bs-input" aria-invalid={!!error} aria-describedby={desc}/>{(error || hint) && <span id={desc} className={cx('bs-field-hint', error && 'bs-error')}>{error || hint}</span>}</div>;
});
export interface TextAreaProps extends React.TextareaHTMLAttributes<HTMLTextAreaElement>, FieldCommon {
}
export const TextArea = forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea({ label, hint, error, id: passedId, className, ...props }, ref) {
    const uid = useId(), id = passedId || uid;
    return <div className={cx('bs-field', className)}><label htmlFor={id}>{label}</label><textarea {...props} id={id} ref={ref} className="bs-input bs-textarea" aria-invalid={!!error} aria-describedby={(hint || error) ? `${id}-hint` : undefined}/>{(hint || error) && <span id={`${id}-hint`} className={cx('bs-field-hint', error && 'bs-error')}>{error || hint}</span>}</div>;
});
export interface SelectFieldProps extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, 'size'>, FieldCommon {
    options: ListOption[];
    searchable?: boolean;
    heading?: string;
}
export function SelectField({ label, options, hint, error, id: passedId, className, searchable, heading, onChange, name, value, defaultValue, disabled, required, form }: SelectFieldProps) {
    const uid = useId(), id = passedId || uid, described = (hint || error) ? `${id}-hint` : undefined;
    return <div className={cx('bs-field', className)}><label htmlFor={id}>{label}{required && <span aria-hidden="true"> *</span>}</label><Listbox id={id} heading={heading || label} name={name} form={form} value={value === undefined ? undefined : String(value)} defaultValue={defaultValue === undefined ? undefined : String(defaultValue)} options={options} disabled={disabled} required={required} error={!!error} searchable={searchable} aria-describedby={described} onChange={event => onChange?.(event as React.ChangeEvent<HTMLSelectElement>)} />{(hint || error) && <span id={`${id}-hint`} className={cx('bs-field-hint', error && 'bs-error')}>{error || hint}</span>}</div>;
}
export const SearchField = forwardRef<HTMLInputElement, Omit<TextFieldProps, 'label'> & {
    label?: string;
}>(function SearchField({ label = 'Search', className, id, ...props }, ref) {
    return <div className={cx('bs-search', className)}><Icon name="search" size={17}/><input {...props} ref={ref} id={id} type="search" aria-label={label} className="bs-input"/></div>;
});
export function Checkbox({ label, description, ...props }: React.InputHTMLAttributes<HTMLInputElement> & {
    label: string;
    description?: string;
}) {
    const id = useId();
    return <label className="bs-choice" htmlFor={id}><input {...props} id={id} type="checkbox"/><span><span>{label}</span>{description && <small>{description}</small>}</span></label>;
}
export interface SwitchProps {
    label: string;
    description?: string;
    checked: boolean;
    onChange: (checked: boolean) => void;
    disabled?: boolean;
}
export function Switch({ label, description, checked, onChange, disabled }: SwitchProps) {
    const id = useId();
    return <div className="bs-switch-row"><span id={id}><strong>{label}</strong>{description && <small>{description}</small>}</span><button type="button" className="bs-switch" role="switch" aria-checked={checked} aria-labelledby={id} disabled={disabled} onClick={() => onChange(!checked)}><span /></button></div>;
}
export interface RadioGroupProps {
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: {
        value: string;
        label: string;
        description?: string;
    }[];
}
export function RadioGroup({ label, value, onChange, options }: RadioGroupProps) {
    const name = useId();
    return <fieldset className="bs-radio-group"><legend>{label}</legend>{options.map(o => <label key={o.value} className={cx('bs-listbox-item', value === o.value && 'is-selected')}><input type="radio" name={name} checked={value === o.value} value={o.value} onChange={() => onChange(o.value)}/><span className="bs-listbox-copy"><span className="bs-listbox-title">{o.label}</span>{o.description && <small>{o.description}</small>}</span>{value === o.value ? <Icon name="check" size={16} className="bs-listbox-check"/> : <span className="bs-listbox-check"/>}</label>)}</fieldset>;
}
export interface SliderProps {
    label: string;
    value: number;
    onChange: (value: number) => void;
    min?: number;
    max?: number;
    step?: number;
    unit?: string;
    disabled?: boolean;
    onReset?: () => void;
}
export function Slider({ label, value, onChange, min = 0, max = 100, step = 1, unit = '', disabled, onReset }: SliderProps) {
    const id = useId();
    const safe = Math.min(max, Math.max(min, Number.isFinite(value) ? value : min));
    return <div className="bs-slider"><label htmlFor={id}>{label}</label><input id={id} type="range" min={min} max={max} step={step} value={safe} disabled={disabled} onChange={e => onChange(Number(e.target.value))} style={{ '--bs-range': `${max === min ? 0 : (safe - min) / (max - min) * 100}%` } as React.CSSProperties}/><output htmlFor={id}>{Number(safe.toFixed(2))}{unit}</output>{onReset && <button className="bs-reset" type="button" onClick={onReset} aria-label={`Reset ${label}`}><Icon name="undo" size={13}/></button>}</div>;
}
export function NumberStepper({ label, value, min = 1, max = 10, disabled = false, onChange }: {
    label: string;
    value: number;
    min?: number;
    max?: number;
    disabled?: boolean;
    onChange: (n: number) => void;
}) {
    const id = useId();
    return <div className="bs-field"><label htmlFor={id}>{label}</label><div className="bs-stepper-input"><Button size="sm" disabled={disabled || value <= min} onClick={() => onChange(Math.max(min, value - 1))} aria-label={`Decrease ${label}`}>−</Button><input id={id} type="number" min={min} max={max} value={value} disabled={disabled} onChange={e => onChange(Math.min(max, Math.max(min, Number(e.target.value) || min)))}/><Button size="sm" disabled={disabled || value >= max} onClick={() => onChange(Math.min(max, value + 1))} aria-label={`Increase ${label}`}>+</Button></div></div>;
}
export interface UploadAreaProps {
    label: string;
    accept?: string;
    maxBytes?: number;
    multiple?: boolean;
    onFiles: (files: File[]) => void;
    compact?: boolean;
}
export function UploadArea({ label, accept = 'image/*', maxBytes = 10 * 1024 * 1024, multiple = false, onFiles, compact = false }: UploadAreaProps) {
    const ref = useRef<HTMLInputElement>(null);
    const [error, setError] = useState('');
    const [over, setOver] = useState(false);
    function receive(files: File[]) {
        const types = accept.split(',').map(t => t.trim());
        const invalid = files.find(f => f.size > maxBytes || !types.some(t => t.startsWith('.') ? f.name.toLowerCase().endsWith(t.toLowerCase()) : t.endsWith('/*') ? f.type.startsWith(t.slice(0, -1)) : f.type === t));
        if (invalid) {
            setError(`${invalid.name}: use ${accept}, up to ${Math.round(maxBytes / 1024 / 1024)} MB per file.`);
            return;
        }
        if (files.length) {
            setError('');
            onFiles(multiple ? files : files.slice(0, 1));
        }
    }
    return <div className={cx('bs-upload', compact && 'bs-upload--compact', over && 'is-over')} onDragOver={e => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={e => { e.preventDefault(); setOver(false); receive(Array.from(e.dataTransfer.files)); }}><input ref={ref} type="file" className="bs-sr-only" accept={accept} multiple={multiple} aria-label={label} onChange={e => { receive(Array.from(e.target.files || [])); e.target.value = ''; }}/><Icon name="upload" size={compact ? 17 : 25}/><strong>{label}</strong>{!compact && <span className="bs-muted bs-small">Drop files here · max {Math.round(maxBytes / 1024 / 1024)} MB</span>}<Button size="sm" onClick={() => ref.current?.click()}>Browse files</Button>{error && <p role="alert" className="bs-error bs-small">{error}</p>}</div>;
}
